import type { GridSortModel, GridFilterModel } from '@mui/x-data-grid';

import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { compareValores } from 'src/auditoria/utils/compare-valores';

import type { TxDataGridProps, FetchRowsParams } from '../types';

export function useGridData<T extends {}>(props: TxDataGridProps<T>) {
  const { fetchRows, rows: clientRows, initialPageSize = 10, initialPage = 0 } = props;

  // Se eu tenho fetchRows, estou em modo server; senão client
  const isServer = useMemo(() => Boolean(fetchRows), [fetchRows]);
  const isClient = useMemo(() => !isServer && Array.isArray(clientRows), [isServer, clientRows]);

  // Controle de página e tamanho
  const [paginationModel, setPaginationModel] = useState({
    page: initialPage,
    pageSize: initialPageSize,
  });
  const { page, pageSize } = paginationModel;

  // Controle de ordenação
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  // Controle de filtro
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  // Rows / rowCount internos
  const [internalRows, setInternalRows] = useState<T[]>([]);
  const [rowCount, setRowCount] = useState(0);

  // Loading / Error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Flag para evitar state updates após unmount
  const activeRef = useRef(true);
  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
    };
  }, []);

  // Sempre que filterModel mudar, reseto page para 0
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [filterModel]);

  const loadData = useCallback(async () => {
    setError(null);
    setLoading(true);

    // Meu critério de ordenação: pega o primeiro sort
    const sort = sortModel[0] ?? {};

    try {
      if (isServer && fetchRows) {
        // Passa page, pageSize, sortField, sortOrder + filterModel pro backend
        const { items, total } = await fetchRows({
          page,
          pageSize,
          sortField: sort.field,
          sortOrder: sort.sort as 'asc' | 'desc',
          filterModel, // ← aqui vai o filtro completo
        } as FetchRowsParams);

        if (activeRef.current) {
          setInternalRows(items);
          setRowCount(total);
        }
      } else if (isClient && clientRows) {
        // Modo client: só ordena e fatia localmente
        await new Promise((resolve) => setTimeout(resolve, 0));

        const data = [...clientRows];
        if (sort.field) {
          data.sort(
            (a, b) =>
              (sort.sort === 'asc' ? 1 : -1) *
              compareValores(a[sort.field as keyof T], b[sort.field as keyof T])
          );
        }

        if (activeRef.current) {
          setInternalRows(data);
          setRowCount(data.length);
        }
      }
    } catch (e) {
      console.error(e);
      if (activeRef.current) {
        setError('Falha ao carregar dados');
      }
    }

    if (activeRef.current) {
      setLoading(false);
    }
  }, [
    fetchRows,
    clientRows,
    page,
    pageSize,
    sortModel,
    filterModel, // ← atenção: loadData roda sempre que filterModel mudar
    isServer,
    isClient,
  ]);

  // Quando page, pageSize, sortModel ou filterModel mudarem, recarrego
  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    rows: internalRows,
    rowCount,
    loading,
    error,
    paginationModel,
    setPaginationModel,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel,
    reload: loadData,
  };
}
